import MicroModal from 'micromodal'
import type { Activity } from '@/scripts/data/types'
import { isMobileOrTablet } from '@/scripts/helpers/features'
import { replaceNonBreakingPrepositions } from '@/scripts/utils/string'

export function isMicroModalOpen(id: string): boolean {
  const el = document.getElementById(id)
  if (!el) return false
  // MicroModal sets aria-hidden="false" and adds .is-open when shown
  return el.getAttribute('aria-hidden') === 'false' || el.classList.contains('is-open')
}

export type ActivityEnv = {
  isMobileOrTablet: boolean
  isIOS: boolean
  analytics?: {
    onStoreRedirectView?: (hash: string) => void
    onStoreRedirectClick?: (hash: string) => void
  }
  openActivity?: (activity: Activity) => void
}

export type ActivityView = {
  setActivity(activity: Activity): void
  getActivity(): Activity | null
  showRoot(): void
  hideRoot(): void
  destroy(): void
}

/**
 * Reusable controller for activity DOM content.
 * - Populates title/description
 * - Switches buttons EXACTLY like your original desktop logic
 * - Wires pointer handlers for navigation / store open + analytics
 * - No positioning/animation here — caller controls that
 */
export function createActivityView(root: HTMLElement, env: ActivityEnv): ActivityView {
  const iconEl = root.querySelector<HTMLImageElement>('.activity-icon')
  const titleEl = root.querySelector<HTMLHeadingElement>('.activity-title')
  const descEl = root.querySelector<HTMLParagraphElement>('.activity-description')
  const actionBtn = root.querySelector<HTMLButtonElement>('.action-button')
  const storeBtn = root.querySelector<HTMLAnchorElement>('.store-button')
  const unlockMsg = root.querySelector<HTMLSpanElement>('.unlock-message')

  let activity: Activity | null = null
  let pendingLinkHash = ''
  let pendingLink = ''

  const toggleHidden = (show: boolean, els?: HTMLElement | (HTMLElement | null)[] | null) => {
    if (!els) return
    const list = Array.isArray(els) ? els : [els]
    for (const el of list) el?.classList[show ? 'remove' : 'add']('hidden')
  }

  const setPendingFromActivity = () => {
    if (!activity) return
    pendingLink = activity.appLink || ''
    pendingLinkHash = activity.linkHash
  }

  const handleActionPointerUp = (e: Event) => {
    e.preventDefault()
    if (!activity) return

    setPendingFromActivity()

    if (activity.isExternalPage) {
      const baseUrl = window.location.origin
      const relativeUrl = `${baseUrl}/${pendingLinkHash}`
      window.open(relativeUrl, '_blank', 'noopener')
      return
    }

    if (pendingLinkHash === 'vanoce-a-pohadky-v-az-kvizu') {
      pendingLinkHash += '&quiz=vanoce-a-pohadky'
    }

    if (env.isMobileOrTablet && pendingLink && storeBtn) {
      env.analytics?.onStoreRedirectClick?.(pendingLinkHash)
      window.open(pendingLink, '_blank', 'noopener')

      return
    }

    window.location.hash = pendingLinkHash

    // Close mobile activity detail (modal)
    if (isMicroModalOpen('modal-activity-detail')) {
      MicroModal.close('modal-activity-detail')
    }

    // Close desktop activity detail (bubble)
    document.dispatchEvent(new CustomEvent('activity-info-box:hide'))

    setTimeout(() => {
      env.openActivity?.(activity!)
    }, 0)
  }

  const updateDom = () => {
    if (!activity) return

    if (titleEl) titleEl.innerText = activity.title
    if (descEl) descEl.innerHTML = replaceNonBreakingPrepositions(activity.description) || ''
    if (iconEl) {
      iconEl.src = ''
      iconEl.src = `${import.meta.env.BASE_URL}images/page/icons/${activity.hasAlt && !activity.isExternalPage ? 'alt/' : ''}${activity.day}.png`
    }

    // Contest + unlock state
    toggleHidden(!activity.isActivityOpen, unlockMsg)

    if (!activity.isActivityOpen) {
      titleEl?.classList.add('closed-activity')
      toggleHidden(true, unlockMsg)
      toggleHidden(false, [actionBtn, storeBtn])
      if (unlockMsg && activity.unlockString) unlockMsg.innerText = activity.unlockString
    } else {
      titleEl?.classList.remove('closed-activity')
      toggleHidden(false, unlockMsg)

      if (isMobileOrTablet && activity.appLink && activity.gameType === 'unity-game' && storeBtn) {
        toggleHidden(false, actionBtn)
        toggleHidden(true, storeBtn)
        env.analytics?.onStoreRedirectView?.(activity.linkHash)
        storeBtn.href = pendingLink
        storeBtn.classList.toggle('ios-button', !!env.isIOS)
      } else {
        toggleHidden(false, storeBtn)
        toggleHidden(true, actionBtn)
      }

      if (actionBtn) actionBtn.textContent = activity.label
    }
  }

  // Wire once
  actionBtn?.addEventListener('click', handleActionPointerUp)
  storeBtn?.addEventListener('click', handleActionPointerUp)

  return {
    setActivity(next: Activity) {
      activity = next
      setPendingFromActivity()
      updateDom()
    },
    getActivity() {
      return activity
    },
    showRoot() {
      toggleHidden(true, root)
    },
    hideRoot() {
      toggleHidden(false, root)
    },
    destroy() {
      actionBtn?.removeEventListener('click', handleActionPointerUp)
      storeBtn?.removeEventListener('click', handleActionPointerUp)
    },
  }
}
